import React, { useState, useEffect, useCallback } from 'react'
import { Bell, BellOff, Settings, CheckCircle, XCircle } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { useAuth } from '../../shared/contexts/AuthContext'
import { PushNotificationService } from '../../lib/services/pushNotificationService'

export default function PushNotificationSettings() {
  const { user } = useAuth()
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [permission, setPermission] = useState<NotificationPermission>('default')
  const [loading, setLoading] = useState(false)
  const [isSupported, setIsSupported] = useState(true)

  const checkNotificationStatus = useCallback(async () => {
    try {
      // Check if push notifications are supported
      if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
        setIsSupported(false)
        return
      }

      const status = await PushNotificationService.getSubscriptionStatus(user!.id)
      setIsSubscribed(status.isSubscribed)
      setPermission(status.permission)
    } catch (error) {
      console.error('Error checking notification status:', error)
    }
  }, [user])

  useEffect(() => {
    if (user) {
      checkNotificationStatus()
    }
  }, [user, checkNotificationStatus])

  const handleSubscribe = async () => {
    if (!user) return

    setLoading(true)
    console.log('Starting subscription process...')
    
    try {
      const success = await PushNotificationService.subscribeToPush(user.id)
      console.log('Subscription result:', success)
      
      if (success) {
        setIsSubscribed(true)
        setPermission('granted')
        toast.success('Push notifications enabled!')
        console.log('Subscription successful')
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('push-subscription-changed'))
        
        // Force a small delay then refresh the page to ensure all components update
        setTimeout(() => {
          window.location.reload()
        }, 500)
      } else {
        toast.error('Failed to enable push notifications')
        console.log('Subscription failed')
      }
    } catch (error) {
      console.error('Error subscribing to push notifications:', error)
      toast.error('Error enabling push notifications: ' + (error as Error).message)
    } finally {
      setLoading(false)
      console.log('Subscription process completed')
    }
  }

  const handleUnsubscribe = async () => {
    if (!user) return

    setLoading(true)
    try {
      const success = await PushNotificationService.unsubscribeFromPush(user.id)
      
      if (success) {
        setIsSubscribed(false)
        toast.success('Push notifications disabled')
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('push-subscription-changed'))
        
        // Force a small delay then refresh the page to ensure all components update
        setTimeout(() => {
          window.location.reload()
        }, 500)
      } else {
        toast.error('Failed to disable push notifications')
      }
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error)
      toast.error('Error disabling push notifications')
    } finally {
      setLoading(false)
    }
  }

  const handleTestNotification = async () => {
    try {
      await PushNotificationService.testNotification()
      toast.success('Test notification sent!')
    } catch (error) {
      console.error('Error sending test notification:', error)
      toast.error('Failed to send test notification')
    }
  }

  const handleDebugSubscriptions = async () => {
    if (!user) return
    try {
      await PushNotificationService.debugSubscriptions(user.id)
      toast.success('Debug info logged to console')
    } catch (error) {
      console.error('Error debugging subscriptions:', error)
      toast.error('Error debugging subscriptions')
    }
  }

  const getStatusIcon = () => {
    if (!isSupported) return <XCircle className="w-5 h-5 text-red-500" />
    if (permission === 'granted' && isSubscribed) return <CheckCircle className="w-5 h-5 text-green-500" />
    if (permission === 'denied') return <XCircle className="w-5 h-5 text-red-500" />
    return <Bell className="w-5 h-5 text-yellow-500" />
  }

  const getStatusText = () => {
    if (!isSupported) return 'Not supported in this browser'
    if (permission === 'granted' && isSubscribed) return 'Enabled'
    if (permission === 'denied') return 'Blocked by browser - Click to reset'
    if (permission === 'default') return 'Not enabled'
    return 'Unknown status'
  }

  if (!user) return null

  return (
    <div className="w-full">
      <div className="flex items-center gap-3 mb-6">
        <Settings className="w-6 h-6 text-gray-600 dark:text-gray-400" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Push Notifications</h3>
      </div>

      {/* Status Display */}
      <div className="flex items-center gap-3 mb-6 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
        {getStatusIcon()}
        <div>
          <p className="font-medium text-gray-900 dark:text-white">
            Status: {getStatusText()}
            {permission === 'denied' && (
              <button
                onClick={() => {
                  if (window.confirm('This will open your browser settings. Click OK to continue.')) {
                    // Try to open browser settings (this may not work in all browsers)
                    if ('permissions' in navigator) {
                      navigator.permissions.query({ name: 'notifications' as PermissionName })
                        .then(result => {
                          console.log('Permission status:', result.state)
                        })
                        .catch(err => {
                          console.log('Could not query permission:', err)
                        })
                    }
                    toast('Please manually reset notification permissions in your browser settings')
                  }
                }}
                className="ml-2 text-xs text-gray-400 hover:text-gray-300 underline"
              >
                Reset
              </button>
            )}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Get notified about new tasks, events, and messages even when the app is closed
          </p>
        </div>
      </div>

      {/* Not Supported Message */}
      {!isSupported && (
        <div className="mb-6 p-4 bg-red-900/20 border border-red-600 rounded-lg">
          <p className="text-red-300">
            Push notifications are not supported in this browser. Please use a modern browser like Chrome, Firefox, or Safari.
          </p>
        </div>
      )}

      {/* Action Buttons */}
      {isSupported && (
        <div className="space-y-4">
          {permission === 'granted' && isSubscribed ? (
            <div className="flex gap-3">
              <button
                onClick={handleUnsubscribe}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-red-900/20 text-red-300 rounded-lg hover:bg-red-800/30 disabled:opacity-50"
              >
                <BellOff className="w-4 h-4" />
                {loading ? 'Disabling...' : 'Disable Notifications'}
              </button>
              <button
                onClick={handleTestNotification}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900/20 text-gray-300 rounded-lg hover:bg-gray-800/30"
              >
                <Bell className="w-4 h-4" />
                Test Notification
              </button>
              <button
                onClick={handleDebugSubscriptions}
                className="flex items-center gap-2 px-4 py-2 bg-stone-900/20 text-stone-300 rounded-lg hover:bg-stone-800/30"
              >
                <Settings className="w-4 h-4" />
                Debug
              </button>
            </div>
          ) : (
            <button
              onClick={handleSubscribe}
              disabled={loading || permission === 'denied'}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
            >
              <Bell className="w-4 h-4" />
              {loading ? 'Enabling...' : 'Enable Notifications'}
            </button>
          )}

          {permission === 'denied' && (
            <div className="p-4 bg-yellow-900/20 border border-yellow-600 rounded-lg">
              <p className="text-yellow-300 text-sm font-medium mb-2">
                Notifications are blocked by your browser. To enable them:
              </p>
              <ol className="text-yellow-300 text-sm list-decimal list-inside space-y-1">
                <li>Click the <strong>lock/info icon</strong> 🔒 in your browser's address bar</li>
                <li>Click <strong>"Site settings"</strong> or <strong>"Site Permissions"</strong></li>
                <li>Find <strong>"Notifications"</strong> and change it from <strong>"Block"</strong> to <strong>"Allow"</strong></li>
                <li><strong>Refresh this page</strong> and try again</li>
              </ol>
              <div className="mt-3 p-2 bg-gray-900/20 border border-gray-600 rounded">
                <p className="text-gray-300 text-xs">
                  <strong>Alternative:</strong> You can also clear site data in Developer Tools → Application → Clear storage
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Information */}
      <div className="mt-6 p-4 bg-gray-900/20 border border-gray-600 rounded-lg">
        <h4 className="font-medium text-gray-300 mb-2">What you'll be notified about:</h4>
        <ul className="text-gray-200 text-sm space-y-1">
          <li>• New tasks assigned to you</li>
          <li>• Upcoming events and reminders</li>
          <li>• New messages from group members</li>
          <li>• Task due dates and overdue notifications</li>
        </ul>
      </div>
    </div>
  )
} 